// Suppliers — outsource partners + purchase orders. Genuinely new module
// (no existing HQ surface), so we own CRUD here.
import { useEffect, useState } from "react";
import { Card } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Edit, Factory, ChevronDown, ChevronRight, AlertTriangle } from "lucide-react";
import { apiClient } from "../../App";
import { fmtMoney, fmtDate, SectionHeader } from "./pccHelpers";

export default function PccSuppliers() {
  const [partners, setPartners] = useState([]);
  const [pos, setPos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [partnerDialog, setPartnerDialog] = useState({ open: false, edit: null });
  const [poDialog, setPoDialog] = useState({ open: false, edit: null });

  async function load() {
    setLoading(true);
    try {
      const [pRes, oRes] = await Promise.all([apiClient.get("/suppliers/partners"), apiClient.get("/suppliers/pos")]);
      setPartners(pRes.data || []);
      setPos(oRes.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load suppliers");
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  async function savePartner(form) {
    try {
      if (partnerDialog.edit?.id) {
        await apiClient.patch(`/suppliers/partners/${partnerDialog.edit.id}`, form);
      } else {
        await apiClient.post("/suppliers/partners", form);
      }
      toast.success("Partner saved");
      setPartnerDialog({ open: false, edit: null });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  }
  async function deletePartner(id) {
    if (!window.confirm("Delete this partner? POs from this partner will remain.")) return;
    try { await apiClient.delete(`/suppliers/partners/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  }
  async function savePo(form) {
    try {
      if (poDialog.edit?.id) {
        await apiClient.patch(`/suppliers/pos/${poDialog.edit.id}`, form);
      } else {
        await apiClient.post("/suppliers/pos", form);
      }
      toast.success("PO saved");
      setPoDialog({ open: false, edit: null });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  }
  async function deletePo(id) {
    if (!window.confirm("Delete this PO?")) return;
    try { await apiClient.delete(`/suppliers/pos/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  }

  if (loading) return <div className="flex items-center justify-center py-20 text-white/50"><Loader2 className="w-5 h-5 animate-spin mr-2" />Loading…</div>;

  return (
    <div className="space-y-5" data-testid="pcc-suppliers">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-xl font-light text-white">Suppliers</h2>
          <p className="text-white/55 text-sm">Outsource partners + purchase orders.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={() => setPartnerDialog({ open: true, edit: null })} variant="outline" size="sm" className="border-white/15 bg-white/[0.04]" data-testid="pcc-new-partner"><Plus className="w-3.5 h-3.5 mr-1" />Partner</Button>
          <Button onClick={() => setPoDialog({ open: true, edit: null })} size="sm" className="bg-amber-500 hover:bg-amber-600 text-black" data-testid="pcc-new-po" disabled={partners.length === 0}><Plus className="w-3.5 h-3.5 mr-1" />Purchase Order</Button>
        </div>
      </div>

      <Card className="bg-[#000000] border-white/[0.08] p-0 overflow-hidden">
        <SectionHeader dot="#2E7D55" title={`Partners (${partners.length})`} />
        <div className="divide-y divide-white/[0.06]">
          {partners.length === 0 ? (
            <div className="px-6 py-10 text-center text-white/40 text-sm">No partners yet. Add your first outsource partner.</div>
          ) : partners.map(p => (
            <PartnerRow key={p.id} partner={p} onEdit={() => setPartnerDialog({ open: true, edit: p })} onDelete={() => deletePartner(p.id)} />
          ))}
        </div>
      </Card>

      <Card className="bg-[#000000] border-white/[0.08] p-0 overflow-hidden">
        <SectionHeader dot="#B96A43" title={`Purchase Orders (${pos.length})`} />
        <div className="divide-y divide-white/[0.06] max-h-[480px] overflow-y-auto">
          {pos.length === 0 ? (
            <div className="px-6 py-10 text-center text-white/40 text-sm">No purchase orders yet.</div>
          ) : pos.map(o => (
            <PoRow key={o.id} po={o} onEdit={() => setPoDialog({ open: true, edit: o })} onDelete={() => deletePo(o.id)} />
          ))}
        </div>
      </Card>

      {partnerDialog.open && <PartnerDialog partner={partnerDialog.edit} onSave={savePartner} onClose={() => setPartnerDialog({ open: false, edit: null })} />}
      {poDialog.open && <PoDialog po={poDialog.edit} partners={partners} onSave={savePo} onClose={() => setPoDialog({ open: false, edit: null })} />}
    </div>
  );
}

function PartnerRow({ partner, onEdit, onDelete }) {
  const [open, setOpen] = useState(false);
  const [logs, setLogs] = useState([]);
  const [cos, setCos] = useState([]);
  const [logText, setLogText] = useState("");
  const [coForm, setCoForm] = useState({ description: "", amount: "" });

  useEffect(() => {
    if (!open) return;
    let alive = true;
    Promise.all([
      apiClient.get(`/pcc/partners/${partner.id}/logs`).then(r => r.data).catch(() => []),
      apiClient.get(`/pcc/partners/${partner.id}/change-orders`).then(r => r.data).catch(() => []),
    ]).then(([l, c]) => { if (alive) { setLogs(l); setCos(c); } });
    return () => { alive = false; };
  }, [open, partner.id]);

  async function addLog() {
    const t = logText.trim(); if (!t) return;
    try { const r = await apiClient.post(`/pcc/partners/${partner.id}/logs`, { text: t }); setLogs([r.data, ...logs]); setLogText(""); }
    catch { toast.error("Failed"); }
  }
  async function addCo() {
    if (!coForm.description || !coForm.amount) return;
    try { const r = await apiClient.post(`/pcc/partners/${partner.id}/change-orders`, { ...coForm, partner_id: partner.id, amount: Number(coForm.amount), status: "pending" }); setCos([r.data, ...cos]); setCoForm({ description: "", amount: "" }); }
    catch { toast.error("Failed"); }
  }
  async function patchCo(co, body) {
    try { const r = await apiClient.patch(`/pcc/supplier-change-orders/${co.id}`, body); setCos(cos.map(x => x.id === co.id ? r.data : x)); }
    catch { toast.error("Failed"); }
  }
  return (
    <div className="border-b border-white/[0.06]">
      <div className="px-6 py-3 flex items-center gap-3" data-testid={`pcc-partner-row-${partner.id}`}>
        <button onClick={() => setOpen(!open)} className="text-white/45 hover:text-white">{open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}</button>
        <div className="w-9 h-9 rounded-lg bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center"><Factory className="w-4 h-4 text-emerald-300" /></div>
        <div className="flex-1 min-w-0">
          <div className="text-sm text-white truncate">{partner.name}</div>
          <div className="text-[11px] text-white/45 font-mono truncate">{partner.type || "Studio"} · {partner.contact_email || "—"} · {(partner.capabilities || []).join(", ") || "—"}</div>
        </div>
        <div className="text-[12px] font-mono text-white/65">{partner.rate_per_hour ? `$${partner.rate_per_hour}/h` : "—"}</div>
        <Button variant="ghost" size="icon" onClick={onEdit}><Edit className="w-4 h-4 text-white/55" /></Button>
        <Button variant="ghost" size="icon" onClick={onDelete}><Trash2 className="w-4 h-4 text-red-400/80" /></Button>
      </div>
      {open && (
        <div className="px-6 py-3 bg-white/[0.02] border-t border-white/[0.04] space-y-4">
          {/* Change Orders */}
          <div>
            <div className="text-[10px] uppercase tracking-wider text-white/40 font-semibold mb-2">Change Orders</div>
            <div className="flex gap-2 mb-2">
              <Input placeholder="Description" value={coForm.description} onChange={(e) => setCoForm({ ...coForm, description: e.target.value })} className="bg-black/30 border-white/10 text-white h-8 text-sm" />
              <Input type="number" placeholder="Amount" value={coForm.amount} onChange={(e) => setCoForm({ ...coForm, amount: e.target.value })} className="bg-black/30 border-white/10 text-white h-8 w-32 text-sm" />
              <Button onClick={addCo} size="sm" className="bg-amber-500 hover:bg-amber-600 text-black h-8">Add</Button>
            </div>
            <div className="space-y-1">
              {cos.length === 0 ? <div className="text-[11px] text-white/40">none</div> : cos.map(co => (
                <div key={co.id} className="flex items-center gap-2 text-[12px] p-1.5 bg-white/[0.02] border border-white/[0.05] rounded">
                  <div className="flex-1 truncate"><span className="text-white">{co.description}</span> <span className="font-mono text-white/55 ml-2">{fmtMoney(co.amount)}</span></div>
                  <select value={co.status} onChange={(e) => patchCo(co, { status: e.target.value })} className="bg-black/30 border border-white/10 text-white text-[11px] rounded px-1.5 py-0.5">
                    <option value="pending">pending</option><option value="approved">approved</option><option value="rejected">rejected</option>
                  </select>
                  <Button variant="ghost" size="icon" className="h-6 w-6" onClick={async () => { try { await apiClient.delete(`/pcc/supplier-change-orders/${co.id}`); setCos(cos.filter(x => x.id !== co.id)); } catch { toast.error("Failed"); } }}><Trash2 className="w-3 h-3 text-red-400/70" /></Button>
                </div>
              ))}
            </div>
          </div>
          {/* Accountability Logs */}
          <div>
            <div className="text-[10px] uppercase tracking-wider text-white/40 font-semibold mb-2">Accountability Log</div>
            <div className="flex gap-2 mb-2">
              <Input placeholder="Issue / note" value={logText} onChange={(e) => setLogText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addLog()} className="bg-black/30 border-white/10 text-white h-8 text-sm" />
              <Button onClick={addLog} size="sm" className="bg-amber-500 hover:bg-amber-600 text-black h-8"><AlertTriangle className="w-3 h-3 mr-1" />Log</Button>
            </div>
            <div className="space-y-1">
              {logs.length === 0 ? <div className="text-[11px] text-white/40">none</div> : logs.map(l => (
                <div key={l.id} className="text-[12px] p-1.5 bg-white/[0.02] border border-white/[0.05] rounded">
                  <div className="text-white">{l.text}</div>
                  <div className="text-[10px] text-white/45 font-mono">{l.author_name} · {fmtDate(l.created_at)}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function PoRow({ po, onEdit, onDelete }) {
  const [open, setOpen] = useState(false);
  const [installments, setInstallments] = useState([]);
  const [form, setForm] = useState({ label: "", amount: "", due_date: "" });

  useEffect(() => {
    if (!open) return;
    let alive = true;
    apiClient.get(`/pcc/pos/${po.id}/installments`).then(r => { if (alive) setInstallments(r.data); }).catch(() => {});
    return () => { alive = false; };
  }, [open, po.id]);

  async function add() {
    if (!form.label || !form.amount) return;
    try { const r = await apiClient.post(`/pcc/pos/${po.id}/installments`, { po_id: po.id, label: form.label, amount: Number(form.amount), due_date: form.due_date || null }); setInstallments([...installments, r.data]); setForm({ label: "", amount: "", due_date: "" }); }
    catch { toast.error("Failed"); }
  }
  async function togglePaid(inst) {
    const next = inst.status === "paid" ? "pending" : "paid";
    try { const r = await apiClient.patch(`/pcc/installments/${inst.id}`, { status: next }); setInstallments(installments.map(x => x.id === inst.id ? r.data : x)); }
    catch { toast.error("Failed"); }
  }
  async function del(inst) {
    try { await apiClient.delete(`/pcc/installments/${inst.id}`); setInstallments(installments.filter(x => x.id !== inst.id)); }
    catch { toast.error("Failed"); }
  }

  return (
    <div className="border-b border-white/[0.06]">
      <div className="px-6 py-3 flex items-center gap-3" data-testid={`pcc-po-row-${po.id}`}>
        <button onClick={() => setOpen(!open)} className="text-white/45 hover:text-white">{open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}</button>
        <span className="text-[11px] font-mono text-white/55 font-bold w-20">{po.short_id}</span>
        <div className="flex-1 min-w-0">
          <div className="text-sm text-white truncate">{po.description}</div>
          <div className="text-[11px] text-white/45 font-mono truncate">{po.partner_name} · {po.project_name || "—"} · {fmtDate(po.issued_at)}</div>
        </div>
        <Badge className={`border ${po.status === "paid" ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" : po.status === "invoiced" ? "bg-blue-500/15 text-blue-300 border-blue-500/30" : "bg-white/[0.06] text-white/55 border-white/15"}`}>{(po.status || "draft").toUpperCase()}</Badge>
        <div className="text-sm font-mono tabular-nums w-28 text-right">{fmtMoney(po.amount)}</div>
        <Button variant="ghost" size="icon" onClick={onEdit}><Edit className="w-4 h-4 text-white/55" /></Button>
        <Button variant="ghost" size="icon" onClick={onDelete}><Trash2 className="w-4 h-4 text-red-400/80" /></Button>
      </div>
      {open && (
        <div className="px-6 py-3 bg-white/[0.02] border-t border-white/[0.04]">
          <div className="text-[10px] uppercase tracking-wider text-white/40 font-semibold mb-2">Installments</div>
          <div className="flex gap-2 mb-2">
            <Input placeholder="Label (e.g. Upfront)" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} className="bg-black/30 border-white/10 text-white h-8 text-sm" />
            <Input type="number" placeholder="Amount" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="bg-black/30 border-white/10 text-white h-8 w-32 text-sm" />
            <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="bg-black/30 border-white/10 text-white h-8 w-36 text-sm" />
            <Button onClick={add} size="sm" className="bg-amber-500 hover:bg-amber-600 text-black h-8">Add</Button>
          </div>
          <div className="space-y-1">
            {installments.length === 0 ? <div className="text-[11px] text-white/40">no installments</div> : installments.map(inst => (
              <div key={inst.id} className="flex items-center gap-2 text-[12px] p-1.5 bg-white/[0.02] border border-white/[0.05] rounded">
                <input type="checkbox" checked={inst.status === "paid"} onChange={() => togglePaid(inst)} className="accent-emerald-400" />
                <div className="flex-1 truncate"><span className="text-white">{inst.label}</span> <span className="text-white/45 ml-2">{fmtDate(inst.due_date)}</span></div>
                <span className={`font-mono ${inst.status === "paid" ? "text-emerald-300" : "text-white"}`}>{fmtMoney(inst.amount)}</span>
                <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => del(inst)}><Trash2 className="w-3 h-3 text-red-400/70" /></Button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function PartnerDialog({ partner, onSave, onClose }) {
  const [form, setForm] = useState({
    name: partner?.name || "",
    type: partner?.type || "Outsource Studio",
    contact_email: partner?.contact_email || "",
    contact_phone: partner?.contact_phone || "",
    capabilities: (partner?.capabilities || []).join(", "),
    rate_per_hour: partner?.rate_per_hour || "",
    notes: partner?.notes || "",
  });
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-[#000000] border-white/[0.08] text-white" data-testid="pcc-partner-dialog">
        <DialogHeader><DialogTitle>{partner ? "Edit Partner" : "New Partner"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-[11px] text-white/55">Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-black/30 border-white/10 text-white mt-1" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-[11px] text-white/55">Type</Label><Input value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="bg-black/30 border-white/10 text-white mt-1" /></div>
            <div><Label className="text-[11px] text-white/55">Rate / hour</Label><Input type="number" value={form.rate_per_hour} onChange={(e) => setForm({ ...form, rate_per_hour: e.target.value })} className="bg-black/30 border-white/10 text-white mt-1" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-[11px] text-white/55">Email</Label><Input value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} className="bg-black/30 border-white/10 text-white mt-1" /></div>
            <div><Label className="text-[11px] text-white/55">Phone</Label><Input value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} className="bg-black/30 border-white/10 text-white mt-1" /></div>
          </div>
          <div><Label className="text-[11px] text-white/55">Capabilities (comma-sep)</Label><Input value={form.capabilities} onChange={(e) => setForm({ ...form, capabilities: e.target.value })} className="bg-black/30 border-white/10 text-white mt-1" /></div>
          <div><Label className="text-[11px] text-white/55">Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="bg-black/30 border-white/10 text-white mt-1" rows={2} /></div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={() => onSave({ ...form, capabilities: form.capabilities.split(",").map(s => s.trim()).filter(Boolean), rate_per_hour: form.rate_per_hour ? Number(form.rate_per_hour) : null })} className="bg-amber-500 hover:bg-amber-600 text-black" data-testid="pcc-partner-save">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PoDialog({ po, partners, onSave, onClose }) {
  const [form, setForm] = useState({
    partner_id: po?.partner_id || partners[0]?.id || "",
    project_name: po?.project_name || "",
    description: po?.description || "",
    amount: po?.amount || "",
    status: po?.status || "draft",
    issued_at: po?.issued_at || new Date().toISOString().slice(0, 10),
    due_at: po?.due_at || "",
    notes: po?.notes || "",
  });
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-[#000000] border-white/[0.08] text-white" data-testid="pcc-po-dialog">
        <DialogHeader><DialogTitle>{po ? "Edit PO" : "New Purchase Order"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-[11px] text-white/55">Partner</Label>
            <Select value={form.partner_id} onValueChange={(v) => setForm({ ...form, partner_id: v })}>
              <SelectTrigger className="bg-black/30 border-white/10 text-white mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>{partners.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-[11px] text-white/55">Project / Program</Label><Input value={form.project_name} onChange={(e) => setForm({ ...form, project_name: e.target.value })} className="bg-black/30 border-white/10 text-white mt-1" /></div>
          <div><Label className="text-[11px] text-white/55">Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="bg-black/30 border-white/10 text-white mt-1" rows={2} /></div>
          <div className="grid grid-cols-3 gap-3">
            <div><Label className="text-[11px] text-white/55">Amount</Label><Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="bg-black/30 border-white/10 text-white mt-1" /></div>
            <div><Label className="text-[11px] text-white/55">Issued</Label><Input type="date" value={form.issued_at} onChange={(e) => setForm({ ...form, issued_at: e.target.value })} className="bg-black/30 border-white/10 text-white mt-1" /></div>
            <div><Label className="text-[11px] text-white/55">Due</Label><Input type="date" value={form.due_at} onChange={(e) => setForm({ ...form, due_at: e.target.value })} className="bg-black/30 border-white/10 text-white mt-1" /></div>
          </div>
          <div><Label className="text-[11px] text-white/55">Status</Label>
            <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
              <SelectTrigger className="bg-black/30 border-white/10 text-white mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>{["draft", "sent", "acknowledged", "invoiced", "paid"].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={() => onSave({ ...form, amount: Number(form.amount || 0) })} className="bg-amber-500 hover:bg-amber-600 text-black" data-testid="pcc-po-save">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
